// src/admin/components/POEditDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import SupplierDialog from '../supplier/SupplierDialog';
import { PurchaseOrderService } from './purchaseOrderService';
import { ProductService } from '../product/productService';
import { Supplier, PurchaseOrderItem, PurchaseOrder } from './PurchaseOrder';
import { Product, ProductVariant } from '../../../enums/product';
import { useAuth } from '../../../contexts/AuthContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { useCompany } from '../../../contexts/CompanyContext';
import { formatCurrency } from '../../../util/Formatcurrency';

interface POEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  purchaseOrder: PurchaseOrder;
  onShowToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const POEditDialog: React.FC<POEditDialogProps> = ({ open, onClose, onSuccess, purchaseOrder, onShowToast }) => {
  const { currentUser } = useAuth();
  const { companyProfile } = useCompany();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const companyCurrency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(companyCurrency);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [items, setItems] = useState<PurchaseOrderItem[]>([]);
  const [taxRate, setTaxRate] = useState('0');
  const [shippingCost, setShippingCost] = useState('0');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);

  useEffect(() => {
    if (open) {
      fetchSuppliers();
      fetchProducts();
      initializeForm();
    }
  }, [open, purchaseOrder]);

  const initializeForm = () => {
    setSelectedSupplier({
      id: purchaseOrder.supplierId,
      name: purchaseOrder.supplierName,
      email: purchaseOrder.supplierEmail,
      phone: purchaseOrder.supplierPhone,
      address: '',
      isActive: true,
    });
    setItems(purchaseOrder.items.map(item => ({ ...item })));
    setTaxRate(purchaseOrder.taxRate.toString());
    setShippingCost(purchaseOrder.shippingCost.toString());
    if (purchaseOrder.expectedDeliveryDate) {
      setExpectedDeliveryDate(new Date(purchaseOrder.expectedDeliveryDate).toISOString().split('T')[0]);
    } else {
      setExpectedDeliveryDate('');
    }
    setNotes(purchaseOrder.notes || '');
    setInternalNotes(purchaseOrder.internalNotes || '');
  };

  const fetchSuppliers = async () => {
    try {
      const data = await PurchaseOrderService.getAllSuppliers();
      setSuppliers(data);
    } catch (err: any) {
      onShowToast('Failed to load suppliers', 'error');
    }
  };

  const fetchProducts = async () => {
    try {
      const data = await ProductService.getProductsForDropdown();
      setProducts(data);
    } catch (err: any) {
      onShowToast('Failed to load products', 'error');
    }
  };

  const handleAddItem = () => {
    setItems([...items, {
      id: `temp-${Date.now()}`,
      productId: '', productName: '', variantId: '', variantName: '',
      sku: '', quantity: 1, unitPrice: 0, totalPrice: 0, receivedQuantity: 0,
    }]);
  };

  const handleRemoveItem = (index: number) => setItems(items.filter((_, i) => i !== index));

  const handleItemChange = (index: number, field: keyof PurchaseOrderItem, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    if (field === 'quantity' || field === 'unitPrice') {
      newItems[index].totalPrice = newItems[index].quantity * newItems[index].unitPrice;
    }
    setItems(newItems);
  };

  const handleProductSelect = (index: number, product: Product | null) => {
    const newItems = [...items];
    if (!product) {
      newItems[index] = { ...newItems[index], productId: '', productName: '', variantId: '', variantName: '', sku: '', unitPrice: 0, totalPrice: 0 };
      setItems(newItems);
      return;
    }
    if (!product.hasVariants || !product.variants || product.variants.length === 0) {
      newItems[index] = { ...newItems[index], productId: product.id!, productName: product.name, variantId: '', variantName: '', sku: product.sku, unitPrice: product.price, totalPrice: newItems[index].quantity * product.price };
    } else {
      newItems[index] = { ...newItems[index], productId: product.id!, productName: product.name, variantId: '', variantName: '', sku: '', unitPrice: 0, totalPrice: 0 };
    }
    setItems(newItems);
  };

  const handleVariantSelect = (index: number, variant: ProductVariant | null) => {
    const newItems = [...items];
    if (!variant) {
      newItems[index] = { ...newItems[index], variantId: '', variantName: '', sku: '', unitPrice: 0, totalPrice: 0 };
      setItems(newItems);
      return;
    }
    const variantName = variant.variantSelections.map(vs => vs.variantValue).join(' - ');
    newItems[index] = { ...newItems[index], variantId: variant.id || '', variantName, sku: variant.sku, unitPrice: variant.price, totalPrice: newItems[index].quantity * variant.price };
    setItems(newItems);
  };

  const getSelectedProduct = (index: number): Product | undefined => products.find(p => p.id === items[index].productId);
  const getProductVariants = (index: number): ProductVariant[] => {
    const product = getSelectedProduct(index);
    if (!product || !product.hasVariants || !product.variants) return [];
    return product.variants;
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
    const tax = subtotal * (parseFloat(taxRate) / 100);
    const shipping = parseFloat(shippingCost) || 0;
    return { subtotal, tax, shipping, total: subtotal + tax + shipping };
  };

  const handleSubmit = async () => {
    if (!currentUser) { onShowToast('You must be logged in to update purchase orders', 'error'); return; }
    if (!selectedSupplier) { setError('Please select a supplier'); onShowToast('Please select a supplier', 'error'); return; }
    if (items.length === 0) { setError('Please add at least one item'); onShowToast('Please add at least one item', 'error'); return; }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.productId) { const msg = `Please select a product for item #${i + 1}`; setError(msg); onShowToast(msg, 'error'); return; }
      const product = products.find(p => p.id === item.productId);
      if (product && product.hasVariants && product.variants && product.variants.length > 0 && !item.variantId) {
        const msg = `Please select a variant for item #${i + 1} (${item.productName})`;
        setError(msg); onShowToast(msg, 'error'); return;
      }
      if (item.quantity <= 0) { const msg = `Please enter a valid quantity for item #${i + 1}`; setError(msg); onShowToast(msg, 'error'); return; }
      if (item.unitPrice <= 0) { const msg = `Please enter a valid unit price for item #${i + 1}`; setError(msg); onShowToast(msg, 'error'); return; }
    }

    const tax = parseFloat(taxRate);
    if (tax < 0 || tax > 100) { setError('Tax rate must be between 0 and 100'); onShowToast('Tax rate must be between 0 and 100', 'error'); return; }
    const shipping = parseFloat(shippingCost);
    if (shipping < 0) { setError('Shipping cost cannot be negative'); onShowToast('Shipping cost cannot be negative', 'error'); return; }

    try {
      setLoading(true);
      setError('');
      await PurchaseOrderService.updatePurchaseOrder(purchaseOrder.id!, {
        supplierId: selectedSupplier.id!,
        items, taxRate, shippingCost,
        expectedDeliveryDate: expectedDeliveryDate ? new Date(expectedDeliveryDate) : null,
        notes, internalNotes,
      });
      onShowToast('Purchase order updated successfully', 'success');
      onSuccess();
      handleClose();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update purchase order';
      setError(errorMessage);
      onShowToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  const handleSupplierDialogSuccess = async () => {
    await fetchSuppliers();
    setSupplierDialogOpen(false);
    onShowToast('Supplier created successfully', 'success');
  };

  const totals = calculateTotals();

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 680, md: 780 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

          {/* Header */}
          <Box sx={{
            px: 3, py: 2,
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <Box>
              <Typography variant="h6">Edit Purchase Order</Typography>
              <Typography variant="body2" color="text.secondary">{purchaseOrder.poNumber}</Typography>
            </Box>
            <IconButton onClick={handleClose} edge="end"><CloseIcon /></IconButton>
          </Box>

          {/* Scrollable Content */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
            )}

            <Grid container spacing={3}>
              {/* Supplier */}
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                  <Autocomplete
                    options={suppliers}
                    getOptionLabel={(option) => option.name}
                    value={selectedSupplier}
                    onChange={(_, value) => setSelectedSupplier(value)}
                    renderInput={(params) => <TextField {...params} label="Supplier *" placeholder="Select supplier" />}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.email} | {option.phone}</Typography>
                        </Box>
                      </li>
                    )}
                    sx={{ flex: 1 }}
                    disabled={loading}
                  />
                  <Button variant="outlined" onClick={() => setSupplierDialogOpen(true)} sx={{ minWidth: 120, height: { sm: 56 }, width: { xs: '100%', sm: 'auto' } }} disabled={loading}>
                    New Supplier
                  </Button>
                </Stack>
              </Grid>

              {/* Items */}
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Order Items</Typography>
                  <Button startIcon={<AddIcon />} onClick={handleAddItem} size="small" variant="outlined" disabled={loading}>
                    Add Item
                  </Button>
                </Stack>

                <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Variant</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>SKU</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Unit Price</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, index) => {
                        const selectedProduct = getSelectedProduct(index);
                        const hasVariants = selectedProduct?.hasVariants && selectedProduct?.variants && selectedProduct.variants.length > 0;
                        const variants = getProductVariants(index);

                        return (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Autocomplete
                                options={products}
                                getOptionLabel={(option) => option.name}
                                value={selectedProduct || null}
                                onChange={(_, value) => handleProductSelect(index, value)}
                                renderInput={(params) => <TextField {...params} size="small" placeholder="Select product" />}
                                renderOption={(props, option) => (
                                  <li {...props}>
                                    <Box>
                                      <Typography variant="body2">{option.name}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {option.hasVariants ? 'Has Variants' : `SKU: ${option.sku}`}
                                      </Typography>
                                    </Box>
                                  </li>
                                )}
                                sx={{ minWidth: { xs: 140, sm: 180 } }}
                                disabled={loading}
                              />
                              {hasVariants && (
                                <Autocomplete
                                  options={variants}
                                  getOptionLabel={(option) => option.variantSelections.map(vs => vs.variantValue).join(' - ')}
                                  value={variants.find(v => v.id === item.variantId) || null}
                                  onChange={(_, value) => handleVariantSelect(index, value)}
                                  renderInput={(params) => <TextField {...params} size="small" placeholder="Select variant" />}
                                  sx={{ minWidth: { xs: 140, sm: 180 }, mt: 1, display: { xs: 'block', sm: 'none' } }}
                                  disabled={loading}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                              {hasVariants ? (
                                <Autocomplete
                                  options={variants}
                                  getOptionLabel={(option) => option.variantSelections.map(vs => vs.variantValue).join(' - ')}
                                  value={variants.find(v => v.id === item.variantId) || null}
                                  onChange={(_, value) => handleVariantSelect(index, value)}
                                  renderInput={(params) => <TextField {...params} size="small" placeholder="Select variant" />}
                                  renderOption={(props, option) => (
                                    <li {...props}>
                                      <Box>
                                        <Typography variant="body2">{option.variantSelections.map(vs => vs.variantValue).join(' - ')}</Typography>
                                        <Typography variant="caption" color="text.secondary">SKU: {option.sku} | {formatCurrency(option.price, currencySymbol)}</Typography>
                                      </Box>
                                    </li>
                                  )}
                                  sx={{ minWidth: 160 }}
                                  disabled={loading}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                              <Typography variant="body2">{item.sku || '-'}</Typography>
                            </TableCell>
                            <TableCell>
                              <TextField type="number" size="small" value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                                inputProps={{ min: 1 }} sx={{ width: 70 }} disabled={loading} />
                            </TableCell>
                            <TableCell>
                              <TextField type="number" size="small" value={item.unitPrice}
                                onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                inputProps={{ min: 0, step: 0.01 }} sx={{ width: 90 }} disabled={loading} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {formatCurrency(item.totalPrice, currencySymbol)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)} disabled={loading}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {items.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                              No items added. Click "Add Item" to get started.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>

              {/* Financial */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Tax Rate (%)" type="number" value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  inputProps={{ min: 0, max: 100, step: 0.01 }} disabled={loading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={`Shipping Cost (${currencySymbol})`} type="number" value={shippingCost}
                  onChange={(e) => setShippingCost(e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }} disabled={loading} />
              </Grid>

              {/* Totals */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Grid container spacing={1}>
                    {[
                      { label: 'Subtotal:', value: formatCurrency(totals.subtotal, currencySymbol) },
                      { label: `Tax (${taxRate}%):`, value: formatCurrency(totals.tax, currencySymbol) },
                      { label: 'Shipping:', value: formatCurrency(totals.shipping, currencySymbol) },
                    ].map(({ label, value }) => (
                      <React.Fragment key={label}>
                        <Grid item xs={6}><Typography variant="body2">{label}</Typography></Grid>
                        <Grid item xs={6} textAlign="right"><Typography variant="body2">{value}</Typography></Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={6}><Typography variant="h6">Total:</Typography></Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography variant="h6" color="primary">{formatCurrency(totals.total, currencySymbol)}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Date & Notes */}
              <Grid item xs={12}>
                <TextField fullWidth label="Expected Delivery Date" type="date" value={expectedDeliveryDate}
                  onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                  InputLabelProps={{ shrink: true }} disabled={loading} />
              </Grid>
              {/* <Grid item xs={12}>
                <TextField fullWidth label="Notes (Visible to Supplier)" multiline rows={3} value={notes}
                  onChange={(e) => setNotes(e.target.value)} disabled={loading} />
              </Grid> */}
              <Grid item xs={12}>
                <TextField fullWidth label="Internal Notes" multiline rows={2} value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)} disabled={loading} />
              </Grid>
            </Grid>
          </Box>

          {/* Footer */}
          <Divider />
          <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
            <Button onClick={handleClose} disabled={loading} startIcon={<CancelIcon />}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !selectedSupplier || items.length === 0}
              startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              {loading ? 'Updating...' : 'Update Purchase Order'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      <SupplierDialog
        open={supplierDialogOpen}
        onClose={() => setSupplierDialogOpen(false)}
        onSuccess={handleSupplierDialogSuccess}
        onShowToast={onShowToast}
      />
    </>
  );
};

export default POEditDialog;